import {
  createAsyncThunk,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit";
import {
  API_VARIABLE_SELECTED_DOCUMENT_ID,
  API_VARIABLE_USER_ID,
  MARK_ROW_READ,
  MARK_ROW_UNREAD,
  ROW_CHILDREN_HIDE,
  ROW_CHILDREN_SHOW,
  ROW_PIN,
  ROW_UNPIN,
  UPDATE_DOCUMENT_AUTHOR,
  UPDATE_DOCUMENT_MARKET,
  VALUES_HTML,
} from "../../api/api-constants";
import { AXIOS } from "../../api/axios";
import {
  DashboardState,
  SelectedDocumentState,
} from "../../common/types/SliceTypes";
import {
  AuthorStatePayload,
  MarketStatePayload,
} from "../../common/types/dashboard/DashboardFunctionalTypes";
import { FieldValueMetadata } from "../../common/types/dashboard/DashboardUITypes";
import { RootState } from "../store";
import { selectFields } from "./metadataSlice";
import { FieldEntity } from "../../common/types/EntityTypes";
import { mapOfflineFieldEntityToFieldEntity } from "../../common/types/Mapper";
import { SIDE_BAR_TREE } from "../../common/constants";

const initial: DashboardState = {
  selectedDocument: {} as SelectedDocumentState,
  selectedTab: "",
  showNotes: true,
  showStatus: true,
  showUploadedDate: true,
  showDocName: true,
  isDragged: false,
  expandedDocumentId: "",
  prevExpandedDocumentId: "",
  isDocumentDeleted: false,
  fieldInEditMode: "",
  textCopiedFromLHS: "",
  dragToSortEnable: false,
  isTabFilterInEditMode: false,
  isUserProfileMenuOpen: false,
  isStatusFilterEnabled: false,
  newParentIDToExpand: "",
  pivotSelectedText: SIDE_BAR_TREE.INBOX,
  sideBarTreeSelected: SIDE_BAR_TREE.INBOX,
  sideBarSelectedParentNode: "",
  sideBarLevelToDocIds: {},
  isDashboardResizing: false,
};

export const dashboardSlice = createSlice({
  name: "dashboard",
  initialState: initial,
  reducers: {
    toggleNotes: (state) => {
      state.showNotes = !state.showNotes;
    },
    toggleStatus: (state) => {
      state.showStatus = !state.showStatus;
    },
    toggleUploadedDate: (state) => {
      state.showUploadedDate = !state.showUploadedDate;
    },
    toggleDocName: (state) => {
      state.showDocName = !state.showDocName;
    },
    toggleIsDragged: (state) => {
      state.isDragged = !state.isDragged;
    },
    setTextCopiedFromLHS: (state, value) => {
      return {
        ...state,
        textCopiedFromLHS: value.payload,
      };
    },
    setUserProfileMenuState: (state, value) => {
      return {
        ...state,
        isUserProfileMenuOpen: value.payload,
      };
    },
    setSelectedTab: (state, value) => {
      return {
        ...state,
        selectedTab: value.payload,
      };
    },
    setFieldInEditMode: (state, value) => {
      return {
        ...state,
        fieldInEditMode: value.payload,
      };
    },
    setDocumentExpanded: (state, value) => {
      const prevDocExpanded = state.expandedDocumentId;
      return {
        ...state,
        expandedDocumentId: value.payload,
        prevExpandedDocumentId: prevDocExpanded,
      };
    },
    setNewParentToExpand: (state, value) => {
      return {
        ...state,
        newParentIDToExpand: value.payload,
      };
    },
    setDeSelectDocument: (state) => {
      return {
        ...state,
        selectedDocument: {} as SelectedDocumentState,
      };
    },
    setUserStatusFilterState: (state, value) => {
      return {
        ...state,
        isStatusFilterEnabled: value?.payload,
      };
    },
    setSelectedDocument: (state, value) => {
      return {
        ...state,
        selectedDocument:
          value.payload?.id === undefined
            ? ({} as SelectedDocumentState)
            : {
                id: value.payload.id,
                name: value.payload.name,
                author: value.payload.author,
                market: value.payload.market,
                notes: value.payload.notes,
                status: value.payload.status,
                values: value.payload.values,
              },
      };
    },
    setDocumentDeleted: (state, value) => {
      return {
        ...state,
        isDocumentDeleted: value.payload,
      };
    },
    setTabFilterEditMode: (state, value) => {
      return {
        ...state,
        isTabFilterInEditMode: value.payload,
      };
    },
    setPivotSelectedText: (state, value) => {
      return {
        ...state,
        pivotSelectedText: value.payload,
      };
    },
    setSideBarSelectedTree: (state, value) => {
      return {
        ...state,
        sideBarTreeSelected: value.payload,
      };
    },
    setSideBarSelectedParentNode: (state, value) => {
      return {
        ...state,
        sideBarSelectedParentNode: value.payload,
      };
    },
    setSideBarLevelToDocIds: (state, value) => {
      return {
        ...state,
        sideBarLevelToDocIds: value.payload,
      };
    },
    setIsDashboardResizing: (state, value) => {
      return {
        ...state,
        isDashboardResizing: value.payload,
      };
    }
  },
  extraReducers(builder) {
    builder.addCase(fetchDocumentHTML.fulfilled, (state, action) => {
      if (action.payload != null && action.payload != undefined) {
        let currentSelectedDocument = { ...state.selectedDocument };
        currentSelectedDocument.html = action.payload.data;
        return {
          ...state,
          selectedDocument: currentSelectedDocument,
        };
      }
    });
  },
});

export const {
  toggleNotes,
  toggleStatus,
  toggleUploadedDate,
  toggleDocName,
  toggleIsDragged,
  setSelectedTab,
  setSelectedDocument,
  setDeSelectDocument,
  setDocumentExpanded,
  setDocumentDeleted,
  setFieldInEditMode,
  setTextCopiedFromLHS,
  setTabFilterEditMode,
  setUserProfileMenuState,
  setUserStatusFilterState,
  setNewParentToExpand,
  setPivotSelectedText,
  setSideBarSelectedTree,
  setSideBarSelectedParentNode,
  setSideBarLevelToDocIds,
  setIsDashboardResizing,
} = dashboardSlice.actions;

export const selectSideBarLevelToDocIds = (state: RootState) =>
  state.dashboard.sideBarLevelToDocIds;

export const selectPivotSelectedText = (state: RootState) =>
  state.dashboard.pivotSelectedText;
export const selectShowNotes = (state: RootState) => state.dashboard.showNotes;

export const selectShowStatus = (state: RootState) =>
  state.dashboard.showStatus;

export const selectShowUploadedDate = (state: RootState) =>
  state.dashboard.showUploadedDate;

export const selectShowDocName = (state: RootState) =>
  state.dashboard.showDocName;

export const selectFieldInEditMode = (state: RootState) =>
  state.dashboard.fieldInEditMode;

export const selectTextCopiedFromLHS = (state: RootState) =>
  state.dashboard.textCopiedFromLHS;

export const selectIsDocumentDeleted = (state: RootState) =>
  state.dashboard.isDocumentDeleted;

export const selectNewParentToExpand = (state: RootState) =>
  state.dashboard.newParentIDToExpand;

export const selectUserProfileMenuState = (state: RootState) =>
  state.dashboard.isUserProfileMenuOpen;

export const selectUserStatusFilterEnabled = (state: RootState) =>
  state.dashboard.isStatusFilterEnabled;

export const selectDraggedLease = (state: RootState) =>
  state.dashboard.isDragged;

// Get the Currrent Tab
export const selectCurrentTab = (state: RootState) =>
  state.dashboard.selectedTab;

// Get the document Value
export const selectSelectedDocumentId = (state: RootState) =>
  state.dashboard.selectedDocument.id;

// Get the document Lease Status
export const selectSelectedDocumentStatus = (state: RootState) =>
  state.dashboard.selectedDocument.status;

//Get Document Value
export const selectSelectedDocumentValues = (state: RootState) =>
  state.dashboard.selectedDocument.values;

export const selectSelectedDocumentAuthor = (state: RootState) =>
  state.dashboard.selectedDocument.author;

export const selectSelectedDocumentMarket = (state: RootState) =>
  state.dashboard.selectedDocument.market;

// Get Document Name
export const selectSelectedDocumentName = (state: RootState) =>
  state.dashboard.selectedDocument.name;

// Get Document HTML
export const selectSelectedDocumentHTML = (state: RootState) =>
  state.dashboard.selectedDocument.html;

// Get the expanded Document Ids
export const selectSelectedDocumentExpandedId = (state: RootState) =>
  state.dashboard.expandedDocumentId;

export const selectPrevDocumentExpandedId = (state: RootState) =>
  state.dashboard.prevExpandedDocumentId;

export const selectSideBarSelectedTree = (state: RootState) =>
  state.dashboard.sideBarTreeSelected;

export const selectSideBarNodeSelected = (state: RootState) =>
  state.dashboard.sideBarSelectedParentNode;

export const selectIsDashboardResizing = (state: RootState) =>
  state.dashboard.isDashboardResizing;

export const selectSelectedDocumentValuesMapped = createSelector(
  [selectFields, selectSelectedDocumentValues],
  (fields, values) => {
    if (values)
      return values.map((value) => {
        const field = fields.find((field) => field.code == value.field);
        if (field) {
          return {
            field: value.field,
            order: field.order,
            value: value.value,
            prevValue: value.prevValue,
            name: field.name,
            multiline: field.multiLine,
            engineValue: value.engineValue,
          } as FieldValueMetadata;
        } else return {} as FieldValueMetadata;
      });
    else return [];
  }
);

export const selectTabFilterEditState = (state: RootState) =>
  state.dashboard.isTabFilterInEditMode;

export default dashboardSlice.reducer;

// Update the Document Author
export const updateDocumentAuthor = createAsyncThunk(
  "/document/author",
  async (payload: AuthorStatePayload) => {
    let url = UPDATE_DOCUMENT_AUTHOR.replace(
      ":documentId",
      payload.documentId
    ).replace(":authorExternalId", payload.authorExternalId);
    if (payload?.isSingle) {
      url = url + '?isSingle=' + payload?.isSingle;
    }
    try {
      const response = await AXIOS.put(url);
      if (response.status == 200) {
        return response.data;
      }
    } catch (err) {
      console.error(err);
    }
  }
);

// Update the Document Market
export const updateDocumentMarket = createAsyncThunk(
  "/user/market",
  async (payload: MarketStatePayload) => {
    try {
      const url = UPDATE_DOCUMENT_MARKET.replace(
        ":documentId",
        payload.documentId
      ).replace(":selectedMarketExternalId", payload.marketExternalId);
      const response = await AXIOS.put(url);
      if (response.status == 200) {
        return response.data;
      }
    } catch (err) {
      console.error(err);
    }
  }
);

export const fetchDocumentHTML = createAsyncThunk(
  "/document/html",
  async (_, { getState, dispatch }) => {
    const s = getState() as RootState;
    const url = VALUES_HTML.replace(
      API_VARIABLE_SELECTED_DOCUMENT_ID,
      s.dashboard.selectedDocument.id
    ).replace(API_VARIABLE_USER_ID, s.user.externalId);
    try {
      const response = await AXIOS.get(url);
      localStorage.setItem("prevAccessedField", "");
      return response.data;
    } catch (err) {
      console.error(err);
    }
  }
);

export const updateRowPinStatus = createAsyncThunk(
  "/document/row/pin-status",
  async (isPinned: boolean, { getState }) => {
    const s = getState() as RootState;
    let url = "";
    if (isPinned)
      url = ROW_PIN.replace(
        API_VARIABLE_SELECTED_DOCUMENT_ID,
        s.dashboard.selectedDocument.id
      );
    else
      url = ROW_UNPIN.replace(
        API_VARIABLE_SELECTED_DOCUMENT_ID,
        s.dashboard.selectedDocument.id
      );
    try {
      const response = await AXIOS.put(url);
      return response.data;
    } catch (err) {
      console.error(err);
    }
  }
);

export const updateRowReadStatus = createAsyncThunk(
  "/document/row/read-status",
  async (isRead: boolean, { getState }) => {
    const s = getState() as RootState;
    let url = "";
    if (isRead)
      url = MARK_ROW_READ.replace(
        API_VARIABLE_SELECTED_DOCUMENT_ID,
        s.dashboard.selectedDocument.id
      );
    else
      url = MARK_ROW_UNREAD.replace(
        API_VARIABLE_SELECTED_DOCUMENT_ID,
        s.dashboard.selectedDocument.id
      );
    try {
      const response = await AXIOS.put(url);
      return response.data;
    } catch (err) {
      console.error(err);
    }
  }
);

export const rowExpandUpdate = createAsyncThunk(
  "/document/row/children-view-status",
  async (parentDocumentId: string, { getState }) => {
    const s = getState() as RootState;
    if (!s.app.isReadOnly) {
      let url = ROW_CHILDREN_SHOW.replace(
        API_VARIABLE_SELECTED_DOCUMENT_ID,
        parentDocumentId
      );
      try {
        const response = await AXIOS.put(url);
        return response.data;
      } catch (err) {
        console.error(err);
      }
    }
  }
);

export const rowCollapseUpdate = createAsyncThunk(
  "/document/row/collapse/children-view-status",
  async (parentDocumentId: string, { getState }) => {
    const s = getState() as RootState;
    if (!s.app.isReadOnly) {
      let url = ROW_CHILDREN_HIDE.replace(
        API_VARIABLE_SELECTED_DOCUMENT_ID,
        parentDocumentId
      );
      try {
        const response = await AXIOS.put(url);
        return response.data;
      } catch (err) {
        console.error(err);
      }
    }
  }
);

export const updateFieldResize = createAsyncThunk(
  "/fields",
  async (fieldData: FieldEntity, { getState }) => {
    const s = getState() as RootState;
    let url =
      process.env.REACT_APP_API_BASE_URL + "/fields/" + s.user.externalId;
    try {
      await AXIOS.post(url, fieldData);
      mapOfflineFieldEntityToFieldEntity({ ...fieldData, isPersisted: true });
    } catch (err) {
      console.error(err);
    }
  }
);

export const downloadDocument = createAsyncThunk(
  "/document/download",
  async (payload: { documentId: string, docName: string }, { getState }) => {
    const state = getState() as RootState;

    const url =
      process.env.REACT_APP_API_BASE_URL +
      "/document/download/" +
      payload.documentId +
      "/" +
      state.user.externalId;

    try {
      const response = await AXIOS.get(url, {
        responseType: "blob", // <— universal, works for pdf/doc/docx/etc.
      });

      // Get filename from Content-Disposition if available
      let filename = payload.docName;
      const disposition = response.headers["content-disposition"];
      
      if (disposition && disposition.includes("filename=")) {
        filename = disposition
          .split("filename=")[1]
          .trim()
          .replace(/"/g, "");
      }

      // Create Blob URL from API data
      const blob = new Blob([response.data]); // backend decides file type
      const blobUrl = URL.createObjectURL(blob);

      // Create a hidden link & click it
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();

      // Clean up
      link.remove();
      URL.revokeObjectURL(blobUrl);

      return true;
    } catch (err) {
      console.error("Download failed:", err);
      throw err;
    }
  }
);
